import { useRoute, RouteProp } from "@react-navigation/native";
import {
  OrderStatus,
  PaymentType,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import {
  GET_EVENT_OCCURRENCES,
  GET_EVENT_OCCURRENCE,
} from "@ticketingplatform/api/dist/event";
import { useOrderReadQuery } from "@ticketingplatform/api/dist/order";
import { TICKET_LIST_QUERY } from "@ticketingplatform/api/dist/ticket";
import useEvictQueries from "hooks/useEvictQueries";
import { EventNavigationParamList } from "navigation/EventNavigation/types";
import { SCREENS } from "navigation/consts";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNumberParams } from "hooks/useNumberParams";

import { UseOrderStatus } from "./types";

export const useOrderStatus: UseOrderStatus = ({
  pollInterval = 1000,
  pollTimeout = 5 * 60 * 1000,
} = {}) => {
  const evictQueries = useEvictQueries();
  const intl = useIntl();
  const [error, setError] = useState<string>();
  const [polling, setPolling] = useState(false);
  const { orderId } = useNumberParams(true);
  const {
    params: { pool },
  } =
    useRoute<
      RouteProp<EventNavigationParamList, typeof SCREENS.OrderDetails>
    >();

  const { data, loading, refetch, startPolling, stopPolling } =
    useOrderReadQuery({
      notifyOnNetworkStatusChange: true,
      variables: {
        id: orderId,
      },
      onError({ message }) {
        setError(message);
        setPolling(false);
        stopPolling();
      },
      onCompleted(newData) {
        const { status, paymentType } = newData?.order || {};

        setError(undefined);

        if (!pool || paymentType !== PaymentType.STRIPE_TERMINAL) {
          return;
        }

        if (status === OrderStatus.COMPLETED) {
          evictQueries([
            GET_EVENT_OCCURRENCES,
            GET_EVENT_OCCURRENCE,
            TICKET_LIST_QUERY,
          ]);
        }

        if (status !== OrderStatus.CHECKOUT) {
          setPolling(false);
          stopPolling();

          return;
        }

        if (polling) {
          return;
        }

        setPolling(true);
        startPolling(pollInterval);
      },
    });

  useEffect(() => {
    if (!polling) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setPolling(false);
      stopPolling();
      setError(
        intl.formatMessage(
          {
            defaultMessage: "Payment status timeout in {value, date, ::mm:ss}",
          },
          { value: pollTimeout }
        )
      );
    }, pollTimeout);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [intl, pollTimeout, polling, stopPolling]);

  return {
    loading,
    polling,
    error,
    order: data?.order || undefined,
    refetch: useCallback(() => refetch(), [refetch]),
  };
};
